import { Component, computed, inject, OnInit, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UserDataRecord as UserDataHeader, UserDataUpsertRequest } from "../../api/user-data-service";
import { ReactiveSearchHomeComponent } from '@nucleus/ui-base';
import { UserData } from "../../api/user-data-service";
import { UserDataService } from '../../services/user-data/user-data.service';
import { UserScope } from '../../api/user-data-service';
import { UserDataFilter } from '../../services/user-data/user-data.filter';
import { UserDataViewComponent } from './user-data-view/user-data-view.component';
import { UserDataEditComponent, UserDataEditDialogData, UserDataEditDialogResult, OrgScopeOption } from './user-data-edit/user-data-edit.component';
import { DataTypeSelectorDialogBoxComponent } from './data-type-selector-dialog-box/data-type-selector-dialog-box.component';
import { FilterAutocompleteComponent } from '@nucleus/ui-base';

interface FilterChip {
  key: string;
  label: string;
  icon: string;
  removable: boolean;
}

/**
 * UserData v2 fields that aren't in the OpenAPI-generated UserDataRecord
 * yet (they will be once the live tasksense-userdata-service is redeployed
 * + the client is regenerated). Local extension keeps the table strongly
 * typed while we straddle the deploy.
 *
 * <p>See Confluence design page 82345987 §2.3 + §7.1.
 */
// The regen'd UserDataHeader (a.k.a. UserDataRecord) declares the v2 fields
// as `T | undefined` — narrower than this UI's "NULL = use type default"
// semantics. Strip each parent's v2 field so we can re-widen with `T | null`.
type V2Field = 'isResource' | 'capacity' | 'reusable' | 'capacityRemaining'
             | 'availability' | 'costPerUse' | 'costCurrency' | 'source';
interface UserDataHeaderV2 extends Omit<UserDataHeader, V2Field> {
  isResource?: boolean | null;
  capacity?: number | null;
  reusable?: boolean | null;
  capacityRemaining?: number | null;
  /** Raw JSON string of the availability policy. NULL = always available. */
  availability?: string | null;
  costPerUse?: number | null;
  costCurrency?: string | null;
  source?: string;
}

/** Shape of the availability JSON stored in user_data.availability. */
interface AvailabilityPolicy {
  timezone?: string;
  days?: string[];
  windows?: { start: string; end: string }[];
}

@Component({
    selector: 'app-data',
    templateUrl: './data.component.html',
    styleUrl: './data.component.css',
    standalone: false
})
export class DataComponent extends ReactiveSearchHomeComponent<UserDataHeader, UserDataFilter> implements OnInit {

  dataTypeFilter      = viewChild<FilterAutocompleteComponent>("dataTypeFilter");
  departmentFilter    = viewChild<FilterAutocompleteComponent>("departmentFilter");
  orgUnitFilter       = viewChild<FilterAutocompleteComponent>("orgUnitFilter");
  financialUnitFilter = viewChild<FilterAutocompleteComponent>("financialUnitFilter");

  displayedColumns = ['type', 'key', 'label', 'resource', 'createTimestamp', 'lastUpdateTimestamp', 'star'];
  dataTypeList:      string[] = [];
  /** Header-strip filter chips still operate on plain code strings — the
   *  filter sidebar autocomplete is already wired to string[]. */
  departmentList:    string[] = [];
  orgUnitList:       string[] = [];
  financialUnitList: string[] = [];
  /** Richer code+name form for the user-data-edit dialog, where the autocomplete
   *  renders "code — name" while persisting just the code. */
  departmentOptions:    OrgScopeOption[] = [];
  orgUnitOptions:       OrgScopeOption[] = [];
  financialUnitOptions: OrgScopeOption[] = [];

  /** Router for the TASK-177 page-route navigation from the data list
   *  ("Add User Data" → /user-data/new; row Edit → /user-data/:id/edit). */
  private appRouter = inject(Router);

  constructor(private userDataService: UserDataService) {
    super(userDataService, "type");
  }

  override createFilter(): UserDataFilter {
    return new UserDataFilter();
  }

  /** Last filter-options payload from the server. Captured on load so
   *  onResetFilter can revert to "all allowed values pre-checked"
   *  instead of an empty filter — matches the user's mental model of
   *  the default state. TASK-182. */
  private lastScope: UserScope | null = null;

  override onResetFilter(): void {
    const f = new UserDataFilter();
    if (this.lastScope) {
      f.departments    = [...(this.lastScope.departments    ?? [])];
      f.orgUnits       = [...(this.lastScope.orgUnits       ?? [])];
      f.financialUnits = [...(this.lastScope.financialUnits ?? [])];
    }
    this.filter.update(() => f);
    this.dataTypeFilter()?.reset();
    this.departmentFilter()?.reset();
    this.orgUnitFilter()?.reset();
    this.financialUnitFilter()?.reset();
  }

  setBoolField(key: 'resource' | 'notResource', value: boolean): void {
    this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { [key]: value }));
  }

  activeFilterChips = computed<FilterChip[]>(() => {
    const f = this.filter();
    const chips: FilterChip[] = [];
    if (!f.resource && !f.notResource) {
      chips.push({ key: 'noKind', label: 'No kind selected', icon: 'visibility_off', removable: false });
    } else if (!f.resource || !f.notResource) {
      chips.push({ key: 'kind', label: f.resource ? 'Resource' : 'Non-resource', icon: 'category', removable: true });
    }
    if (f.dataTypes?.length) {
      chips.push({ key: 'dataTypes', label: f.dataTypes.join(', '), icon: 'label', removable: true });
    }
    if (f.departments?.length) {
      chips.push({ key: 'departments', label: f.departments.join(', '), icon: 'groups', removable: true });
    }
    if (f.orgUnits?.length) {
      chips.push({ key: 'orgUnits', label: f.orgUnits.join(', '), icon: 'corporate_fare', removable: true });
    }
    if (f.financialUnits?.length) {
      chips.push({ key: 'financialUnits', label: f.financialUnits.join(', '), icon: 'account_balance', removable: true });
    }
    return chips;
  });

  hasRemovableChips(): boolean { return this.activeFilterChips().some(c => c.removable); }

  removeFilterChip(key: string): void {
    switch (key) {
      case 'kind':
        this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { resource: true, notResource: true }));
        break;
      case 'dataTypes':
        this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { dataTypes: [] }));
        this.dataTypeFilter()?.reset();
        break;
      case 'departments':
        this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { departments: [] }));
        this.departmentFilter()?.reset();
        break;
      case 'orgUnits':
        this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { orgUnits: [] }));
        this.orgUnitFilter()?.reset();
        break;
      case 'financialUnits':
        this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, { financialUnits: [] }));
        this.financialUnitFilter()?.reset();
        break;
    }
  }

  ngOnInit(): void {
    if (!this.permissionChecker.hasAccessToUserDataManagement()) {
      this.showAccessDeny();
      this.navigateTo('/home', null);
    } else {
      this.loadDataTypes();
    }
  }

  loadDataTypes(): void {
    this.userDataService.getUserDataTypes({
      onStart: () => this.proggressBarMode.set('buffer'),
      onSuccess: (result: string[]) => {
        if (result != null) { this.dataTypeList = result; }
      },
      onError: (error: string) => this.showError(error),
      onComplete: () => this.proggressBarMode.set('determinate')
    });
    this.loadOrgScopeAutocompletes();
    super.refreshData(true);
  }

  /** UF2 (TASK-178) + TASK-182. Loads the three org-scoping autocompletes
   *  from GET /userdata/filter-options — the caller's authorized units
   *  resolved server-side from the JWT 'units' claim through org_unit.
   *  Admin path (allUnits=true) populates from the org_unit catalog
   *  server-side (TASK-182 backend); on older deploys where the arrays
   *  come back empty for admin, fall back to the per-axis findAll*
   *  endpoints so the UI works against either backend version.
   *
   *  Per-axis failures are silently absorbed — users can still type
   *  freely. Default-checks all allowed values on first load. */
  private loadOrgScopeAutocompletes(): void {
    this.userDataService.getFilterOptions({
      onStart: () => {}, onError: () => {}, onComplete: () => {},
      onSuccess: (scope: UserScope) => {
        if (scope.allUnits
            && !scope.departments?.length
            && !scope.orgUnits?.length
            && !scope.financialUnits?.length) {
          this.userDataService.getDepartments({
            onStart: () => {}, onError: () => {}, onComplete: () => {},
            onSuccess: (codes: string[]) => this.applyScope({
              ...scope, departments: codes
            } as UserScope)
          });
          this.userDataService.getOrgUnits({
            onStart: () => {}, onError: () => {}, onComplete: () => {},
            onSuccess: (codes: string[]) => this.applyScope({
              ...this.lastScope ?? scope, orgUnits: codes
            } as UserScope)
          });
          this.userDataService.getFinancialUnits({
            onStart: () => {}, onError: () => {}, onComplete: () => {},
            onSuccess: (codes: string[]) => this.applyScope({
              ...this.lastScope ?? scope, financialUnits: codes
            } as UserScope)
          });
          return;
        }
        this.applyScope(scope);
      }
    });
  }

  /** Populate the autocomplete sources from a scope payload, capture
   *  the scope for Reset Filters, and seed the filter signal with all
   *  allowed values on first load. TASK-182. Called from the admin
   *  findAll* fallback once per axis as responses arrive — each call
   *  merges into the existing lastScope (so a later axis doesn't lose
   *  the earlier axis's lists). */
  private applyScope(scope: UserScope): void {
    const toOptions = (codes: string[] | undefined): OrgScopeOption[] =>
      (codes ?? []).slice().sort((a, b) => a.localeCompare(b))
        .map(code => ({ code, name: code }));

    this.departmentOptions    = toOptions(scope.departments);
    this.orgUnitOptions       = toOptions(scope.orgUnits);
    this.financialUnitOptions = toOptions(scope.financialUnits);
    this.departmentList    = this.departmentOptions.map(o => o.code);
    this.orgUnitList       = this.orgUnitOptions.map(o => o.code);
    this.financialUnitList = this.financialUnitOptions.map(o => o.code);

    this.lastScope = scope;
    const current = this.filter();
    const untouched = !current.departments?.length
                   && !current.orgUnits?.length
                   && !current.financialUnits?.length;
    if (untouched) {
      this.filter.update(f => Object.assign(Object.create(Object.getPrototypeOf(f)), f, {
        departments:    [...this.departmentList],
        orgUnits:       [...this.orgUnitList],
        financialUnits: [...this.financialUnitList]
      }));
    }
  }

  onPurgeAllData(): void {
    if (!this.permissionChecker.hasAccessToPurgeUserData()) { super.showAccessDeny(); return; }
    this.confirm('Confirmation', `You are purging all user data. Are you sure to continue?`)
      .then((confirmation) => {
        if (confirmation) {
          this.userDataService.purge({
            onStart: () => {},
            onSuccess: () => {
              this.loadDataTypes();
              super.showSuccess(`User data successfully purged`);
            },
            onError: (error: string) => super.showError(error),
            onComplete: () => super.refreshData(true),
          });
        }
      });
  }

  onPurgeDataByType(): void {
    if (!this.permissionChecker.hasAccessToPurgeUserData()) { super.showAccessDeny(); return; }
    const dialogRef = this.dialog.open(DataTypeSelectorDialogBoxComponent, {
      panelClass: 'bw-dialog-panel',
      width: "560px",
      data: {
        dataTypeList: this.dataTypeList,
        title: 'Purge By Type',
        subtitle: 'Permanently delete every UserData row of the selected type.',
        icon: 'delete_sweep',
        destructive: true,
        warningTitle: 'This action cannot be undone',
        warningMessage:
            'Every UserData row of the selected type will be permanently '
          + 'deleted. Make sure no scheduled tasks depend on this type before '
          + 'continuing.',
        confirmLabel: 'Purge',
        confirmIcon: 'delete_forever',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined && result != "") {
        this.onPurgeDataBySelectedType(result);
      }
    });
  }

  onPurgeDataBySelectedType(type: string): void {
    if (!this.permissionChecker.hasAccessToPurgeUserData()) { super.showAccessDeny(); return; }
    this.confirm('Confirmation', `You are purging all user data for type ${type}. Are you sure to continue?`)
      .then((confirmation) => {
        if (confirmation) {
          this.userDataService.purgeByType(type, {
            onStart: () => {},
            onSuccess: () => {
              this.loadDataTypes();
              super.showSuccess(`User data for type ${type} successfully purged`);
            },
            onError: (error: string) => super.showError(error),
            onComplete: () => super.refreshData(true),
          });
        }
      });
  }

  // ── Resource column rendering (UserData v2) ────────────────────────────
  //
  // The list column shows different chrome depending on (effective) row state:
  //   * non-resource           → em-dash "—"
  //   * reusable + always-on   → 🖥 {capacity} {unit}              · {cost}/use
  //   * reusable + restricted  → 🖥 {capacity} {unit} · {window}   · {cost}/use
  //   * burn-down              → 🪫 {remaining}/{capacity} {unit} · {cost}/use
  //
  // Effective values: row's own override → type's default → null. The row
  // alone doesn't know its type's defaults yet (a follow-up will fetch
  // /userdata/types/{type}/options for fallback resolution); for now the
  // renderer treats null is-resource as non-resource. That's safe — false
  // negatives just hide the row's resource info, never invents a wrong one.

  /** True when the row is an effective resource. Currently row-only — type
   *  default fallback is a follow-up once we wire the type-options cache. */
  isResourceRow(row: UserDataHeaderV2): boolean {
    if (row.isResource != null) return row.isResource;
    return row.resource;          // legacy v1 mirror
  }

  /** Compose the capacity portion of the cell — `8 slots`, `12/100 calls`, etc.
   *  Returns empty string when capacity is missing. */
  formatCapacity(row: UserDataHeaderV2): string {
    if (row.capacity == null) return '';
    const unit = ''; // unit comes from type defaults — wired in P2.2 once
                    // we have the type-options cache. Until then, just the number.
    if (row.reusable === false && row.capacityRemaining != null) {
      return `${row.capacityRemaining}/${row.capacity}${unit ? ' ' + unit : ''}`;
    }
    return `${row.capacity}${unit ? ' ' + unit : ''}`;
  }

  /** Parse the availability JSON and return a one-line summary, or empty
   *  string when the row is always available (NULL). Garbage JSON renders
   *  as empty so a single bad row can't break the list. */
  formatAvailability(row: UserDataHeaderV2): string {
    if (!row.availability) return '';
    let policy: AvailabilityPolicy;
    try {
      policy = JSON.parse(row.availability);
    } catch {
      return '';
    }
    const parts: string[] = [];
    if (policy.days?.length) parts.push(this.summarizeDays(policy.days));
    if (policy.windows?.length) {
      const w = policy.windows[0];
      const more = policy.windows.length > 1 ? ` (+${policy.windows.length - 1})` : '';
      parts.push(`${w.start}–${w.end}${more}`);
    }
    if (policy.timezone) parts.push(this.shortTimezone(policy.timezone));
    return parts.join(' ');
  }

  /** "M-F" for the 5-day common case, otherwise comma-joined day codes. */
  private summarizeDays(days: string[]): string {
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    if (days.length === 5 && weekdays.every(d => days.includes(d))) return 'M-F';
    if (days.length === 7) return 'daily';
    return days.map(d => d.substring(0, 1)).join('');
  }

  /** Drop the "America/" prefix and trim the rest — "America/Toronto" → "ET-ish".
   *  Just shows the IANA tail; full prefix is too long for a table cell. */
  private shortTimezone(tz: string): string {
    const slash = tz.lastIndexOf('/');
    return slash >= 0 ? tz.substring(slash + 1).replace('_', ' ') : tz;
  }

  /** Compose the cost portion of the cell — `$0.50/use`, `0.50 USD/use`, etc.
   *  Empty when costPerUse is missing. */
  formatCost(row: UserDataHeaderV2): string {
    if (row.costPerUse == null) return '';
    const currency = row.costCurrency ?? '';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
    if (symbol) return `${symbol}${row.costPerUse}/use`;
    return `${row.costPerUse}${currency ? ' ' + currency : ''}/use`;
  }

  /** Material icon for the resource type — desktop/dns = pool/reusable,
   *  battery bar = burn-down, dash = non-resource. */
  resourceIcon(row: UserDataHeaderV2): string {
    if (!this.isResourceRow(row)) return '';
    if (row.reusable === false) return 'battery_3_bar';
    return 'dns';
  }

  /** Hover-tooltip text on the Resource column — explains the row's
   *  semantics at a glance: pool / burn-down / not a resource. */
  resourceTooltip(row: UserDataHeaderV2): string {
    if (!this.isResourceRow(row)) {
      return 'Not a resource — runners do not lock this row exclusively';
    }
    if (row.reusable === false) {
      return 'Resource (burn-down) — each claim consumes capacity';
    }
    return 'Resource (pool) — capacity replenishes on release';
  }

  onViewData(data: UserDataHeader): void {
    if (!data?.type || !data?.key) return;
    this.userDataService.getByTypeAndKey(data.type, data.key, {
      onStart: () => {},
      onSuccess: (result: UserData) => this.showDataOnDialog(result),
      onError: (error: string) => super.showError(error),
      onComplete: () => {},
    });
  }

  private showDataOnDialog(userData: UserData): void {
    this.dialog.open(UserDataViewComponent, {
      panelClass: 'bw-dialog-panel',
      width: "60%",
      data: { userData: userData }
    });
  }

  /** "Add User Data" page-header action — navigates to the full-page
   *  create route (TASK-177). The dialog code below stays in place but is
   *  no longer the create path from the list. */
  onCreate(): void {
    if (!this.permissionChecker.hasAccessToCreateUserData()) { super.showAccessDeny(); return; }
    this.appRouter.navigate(['/scheduling/user-data/new']);
  }

  /** Per-row edit. Navigates to the full-page editor for this row's id
   *  (TASK-177). The dialog opener below is preserved as the create/edit
   *  fallback for any callers we haven't migrated yet. */
  onEdit(header: UserDataHeader): void {
    if (!this.permissionChecker.hasAccessToUpdateUserData()) { super.showAccessDeny(); return; }
    if (header?.id == null) return;
    this.appRouter.navigate(['/scheduling/user-data', header.id, 'edit']);
  }

  /** Open the row's UserData type in the type-admin page. Permission is
   *  gated by the template (hasAccessToUserDataTypeManagement). Saves the
   *  operator a trip through the type-list when they want to inspect or
   *  edit the type's declared attributes / options.
   *
   *  Carries ?from=user-data so the type-detail page's Back button routes
   *  back to /scheduling/user-data instead of /administration/userdata-types
   *  — i.e. back to where the operator came from, not the default
   *  type-list landing page. */
  onOpenType(header: UserDataHeader): void {
    if (!this.permissionChecker.hasAccessToUserDataTypeManagement()) { super.showAccessDeny(); return; }
    if (!header?.type) return;
    this.appRouter.navigate(['/administration/userdata-types', header.type],
        { queryParams: { from: 'user-data' } });
  }

  /** Legacy dialog-based edit — retained as a fallback while the page route
   *  bakes. Reachable via @deprecated; no UI surface today. */
  private onEditViaDialog(header: UserDataHeader): void {
    this.userDataService.getById(header.id, {
        onStart: () => {},
        onSuccess: (full: UserData) => this.openEditDialog(full),
        onError: (error: string) => super.showError(error),
        onComplete: () => {},
    });
  }

  private openEditDialog(userData: UserData): void {
    const ref = this.dialog.open<UserDataEditComponent, UserDataEditDialogData, UserDataEditDialogResult>(
        UserDataEditComponent, {
            panelClass: 'bw-dialog-panel',
            width: '720px',
            data: {
                mode: 'edit',
                userData,
                knownTypes: this.dataTypeList,
                knownDepartments: this.departmentOptions,
                knownOrgUnits: this.orgUnitOptions,
                knownFinancialUnits: this.financialUnitOptions,
            },
        });
    ref.afterClosed().subscribe(req => {
        if (!req) { return; }
        if (!this.permissionChecker.hasAccessToUpdateUserData()) { super.showAccessDeny(); return; }
        this.userDataService.put(userData.type!, userData.key!, req, {
            onStart: () => {},
            onSuccess: (updated: UserData) => super.showSuccess(`User data ${updated.type}:${updated.key} updated`),
            onError: (error: string) => super.showError(error),
            onComplete: () => super.refreshData(true),
        });
    });
  }

  /** Per-row delete with confirmation. */
  onDelete(header: UserDataHeader): void {
    if (!this.permissionChecker.hasAccessToDeleteUserData()) { super.showAccessDeny(); return; }
    this.confirm('Delete user data',
            `Permanently delete <b>${header.type}:${header.key}</b>? This cannot be undone.`)
        .then(confirmed => {
            if (!confirmed) { return; }
            this.userDataService.deleteByTypeAndKey(header.type!, header.key!, {
                onStart: () => {},
                onSuccess: () => super.showSuccess(`User data ${header.type}:${header.key} deleted`),
                onError: (error: string) => super.showError(error),
                onComplete: () => super.refreshData(true),
            });
        });
  }

  /** True when {@code row} is a burn-down resource — eligible for the reset
   *  capacity admin action. Pool rows replenish on release, so they don't
   *  surface the reset; non-resource rows don't have capacity semantics. */
  isResettableBurnDown(row: UserDataHeaderV2): boolean {
    return this.isResourceRow(row)
        && row.reusable === false
        && row.capacity != null;
  }

  /** Per-row reset of capacity_remaining → capacity. POSTs to the admin
   *  reset endpoint; refreshes the list on success. */
  onResetCapacity(row: UserDataHeaderV2): void {
    if (!this.permissionChecker.hasAccessToUpdateUserData()) { super.showAccessDeny(); return; }
    const remaining = row.capacityRemaining ?? row.capacity;
    this.confirm('Reset capacity',
            `Restore <b>${row.type}:${row.key}</b>'s capacity_remaining to `
            + `<b>${row.capacity}</b> `
            + `(currently ${remaining}/${row.capacity})? `
            + `Use after replenishing the underlying inventory / quota.`)
        .then(confirmed => {
            if (!confirmed) { return; }
            this.userDataService.resetCapacity(row.id!, {
                onStart: () => {},
                onSuccess: (updated: UserData) =>
                    super.showSuccess(
                        `User data ${updated.type}:${updated.key} capacity reset to `
                        + `${updated.capacity}.`),
                onError: (error: string) => super.showError(error),
                onComplete: () => super.refreshData(true),
            });
        });
  }
}
